import { Formik } from 'formik'
import { debounce, isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import Card from '@/components/Card'
import DeleteConfirmationOverlay from '@/components/DeleteConfirmationOverlay'
import Text from '@/components/Form/Text'
import { Search } from '@/components/Icons'
import PaginationTable from '@/components/PaginationTable'

import columns from './columns'
import messages from './messages'

import './index.scss'

import { usePrevious } from 'react-use'

const defaultSort = {
  'name.familyName': {
    direction: 'asc',
    position: 2
  }
} // reference to the sorting columns

const searchDelay = 300

function OrganizationUsers(props: any) {
  const intl = useIntl()

  const [state, _setState] = useState({
    filterValue: props.filterValue,
    userToDelete: undefined,
    isDeleting: false,
    message: undefined
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  // componentDidMount
  useEffect(() => {
    initialPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!prevProps) return

    if (
      prevProps.sortingColumn !== props.sortingColumn ||
      (prevProps.loadInitialPage !== props.loadInitialPage && props.loadInitialPage)
    ) {
      initialPage()
    }

    if (prevProps.isDeleting !== props.isDeleting && !props.isDeleting) {
      setState({
        message: <FormattedMessage {...messages.loadingUsers} />
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevProps, props])

  function initialPage() {
    if (!props.sortingColumn) {
      props.loadPage(
        1,
        Object.keys(defaultSort)[0],
        // @ts-expect-error
        defaultSort[Object.keys(defaultSort)[0]].direction.toUpperCase(),
        state.filterValue
      )
    }
  }

  const onRemoveFromOrganization = async () => {
    const userToDelete = state.userToDelete
    setState({
      isDeleting: false,
      userToDelete: '',
      message: <FormattedMessage {...messages.remove} />
    })

    await props.onRemoveFromOrganization(userToDelete)
  }

  const showConfirmDelete = (userId: any) => {
    setState({ isDeleting: true, userToDelete: userId })
  }

  const onCancelDelete = () => {
    setState({ isDeleting: false, userToDelete: '' })
  }

  const onFilterChange = (e: any) => {
    setState({ filterValue: e.target.value })

    if (!!props.sortingColumn && !isEmpty(props.sortingColumn)) {
      fetchFilterResults(
        1,
        Object.keys(props.sortingColumn)[0],
        props.sortingColumn[Object.keys(props.sortingColumn)[0]].direction.toUpperCase()
      )
    } else {
      fetchFilterResults(1)
    }
  }

  const fetchFilterResults = debounce((page: any, sort?: any, sortBy?: any) => {
    props.loadPage(page, sort, sortBy, state.filterValue)
  }, searchDelay)

  const onSelect = (page: any, sort: any, sortBy: any) => {
    setState({
      message: <FormattedMessage {...messages.loadingUsers} />
    })
    props.loadPage(page, sort, sortBy, state.filterValue)
  }

  const sortingColumn = props.sortingColumn || defaultSort

  return (
    <Card
      className="organization-users"
      isActionInProgress={props.isLoading || props.isDeleting}
      actionMessage={state.message}
    >
      <Card.Content>
        <div className="user-search">
          {/* @ts-expect-error */}
          <Formik initialValues={{ search: state.filterValue }}>
            <Text
              icon={props.isLoading && state.filterValue ? <span className="field__icon--loading" /> : <Search />}
              name="search"
              onChange={onFilterChange}
              placeholder={intl.formatMessage(messages.search)}
            />
          </Formik>
        </div>

        {props.users && props.users.length > 0 ? (
          <PaginationTable
            columns={columns({
              currentUser: props.currentUser,
              intl: intl,
              navigateToEditUser: props.navigateToEditUser,
              showConfirmDelete: showConfirmDelete
            })}
            pagination={{
              count: props.userCount,
              startIndex: props.startIndex,
              perPage: props.perPage
            }}
            onPaginateSelect={onSelect}
            rows={props.users}
            rowItemType={<FormattedMessage {...messages.users} />}
            rowKey="id"
            sortingColumn={sortingColumn}
          />
        ) : props.isLoading ? (
          <div>
            <FormattedMessage {...messages.loading} />
          </div>
        ) : (
          <div>
            <FormattedMessage {...messages.noUsers} />
          </div>
        )}
      </Card.Content>
      {state.isDeleting && (
        <DeleteConfirmationOverlay
          onConfirm={onRemoveFromOrganization}
          onCancel={onCancelDelete}
          message={<FormattedMessage {...messages.delete} />}
          subMessage={<FormattedMessage {...messages.deleteSubMessage} />}
        />
      )}
    </Card>
  )
}

OrganizationUsers.displayName = 'OrganizationUsers'

OrganizationUsers.propTypes = {
  currentUser: PropTypes.object.isRequired,
  startIndex: PropTypes.number.isRequired,

  isLoading: PropTypes.bool,
  isLoadingUsersComplete: PropTypes.bool,
  isDeleting: PropTypes.bool,
  loadPage: PropTypes.func.isRequired,
  loadInitialPage: PropTypes.bool,
  navigateToEditUser: PropTypes.func.isRequired,
  onRemoveFromOrganization: PropTypes.func.isRequired,
  perPage: PropTypes.number.isRequired,
  sortingColumn: PropTypes.object,
  userCount: PropTypes.number.isRequired,
  users: PropTypes.arrayOf(PropTypes.object),
  filterValue: PropTypes.any
}

OrganizationUsers.defaultProps = {
  isLoading: false,
  isLoadingUsersComplete: false,
  isDeleting: false,
  loadInitialPage: false,
  sortingColumn: null,
  users: []
}

export default OrganizationUsers
